import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { usePfErrorUtils } from '@atlassian/proforma-common-core/jira-common-context';
import {
	type Form,
	AddForm,
	AddFormStoreContainer,
} from '@atlassian/proforma-form-system/add-form';
import { useAddForm } from '../../../controllers/use-add-form/index.tsx';
import { useHandleFormSelected as useHandleFormSelectedWithFiFState } from '../../../controllers/use-handle-form-selected/index.tsx';
import { useGetAvailableFormsService } from '../../../services/get-available-forms/index.tsx';
import type { JiraAddFormProps } from './types.tsx';

export const JiraAddForm = ({
	addFormButtonClickedCount,
	issueViewStore,
	disableIssueViewFullPageDropzoneCallback,
	issueId,
}: JiraAddFormProps) => {
	const addFormCallback = useAddForm(issueViewStore, disableIssueViewFullPageDropzoneCallback);
	const handleFormSelected = useHandleFormSelectedWithFiFState(
		issueViewStore,
		disableIssueViewFullPageDropzoneCallback,
	);
	const { fetchAvailableForms } = useGetAvailableFormsService(issueId);
	const errorUtils = usePfErrorUtils();

	const [showAddFormPanel, setShowAddFormPanel] = useState<boolean>(false);

	const buttonClickCountRef = useRef(0);
	useEffect(() => {
		// handleFormSelected can get refreshed when the selectedFormStore state changes, so we want to exclude this
		// from triggering itself. Only call handleFormSelected when addFormButtonClickedCount updates
		if (addFormButtonClickedCount > buttonClickCountRef.current) {
			buttonClickCountRef.current = addFormButtonClickedCount;

			handleFormSelected();
			setShowAddFormPanel(true);
		}
	}, [addFormButtonClickedCount, handleFormSelected, setShowAddFormPanel]);

	const handleAddForm = useCallback(
		(formToAdd: Form) => {
			setShowAddFormPanel(false);
			addFormCallback(formToAdd);
		},
		[addFormCallback, setShowAddFormPanel],
	);

	const handleCancelAddForm = useCallback(() => {
		setShowAddFormPanel(false);
	}, [setShowAddFormPanel]);

	const addFormApi = useMemo(
		() => ({
			loadForms: fetchAvailableForms,
		}),
		[fetchAvailableForms],
	);

	return (
		<>
			{showAddFormPanel && (
				<AddFormContainer>
					<AddFormStoreContainer api={addFormApi} errorUtils={errorUtils}>
						<AddForm onAdd={handleAddForm} onCancel={handleCancelAddForm} />
					</AddFormStoreContainer>
				</AddFormContainer>
			)}
		</>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/design-system/use-primitives -- To migrate as part of go/ui-styling-standard
const AddFormContainer = styled.div({
	marginTop: token('space.100'),
});
